/**
 * Per-agent texture-color cache (keyed by chatbot_id, persisted in localStorage).
 *
 * The chat panel's `data-agent-color` drives the send-button + tint color, but
 * it's only known once the bot fetch resolves — causing a flash of the default
 * (blue) color on entry. Priming this cache (e.g. from the agents page, which
 * already knows each agent's color) lets the panel paint the correct color on
 * the very first frame.
 */

import { resolveAgentTextureColor } from '@/brand/textures';

const KEY = 'chat:agentColor';

export function readCachedAgentColor(chatbotId?: string | null): string | null {
  if (!chatbotId) return null;
  try {
    const map = JSON.parse(localStorage.getItem(KEY) || '{}');
    return typeof map?.[chatbotId] === 'string' ? map[chatbotId] : null;
  } catch {
    return null;
  }
}

export function writeCachedAgentColor(chatbotId: string, color: string): void {
  if (!chatbotId || !color) return;
  try {
    const map = JSON.parse(localStorage.getItem(KEY) || '{}');
    if (map[chatbotId] !== color) {
      map[chatbotId] = color;
      localStorage.setItem(KEY, JSON.stringify(map));
    }
  } catch {
    /* ignore quota / parse errors */
  }
}

/** Resolve an agent's avatar_color + name to a texture color and cache it. */
export function primeAgentColor(
  chatbotId: string | null | undefined,
  avatarColor: string | null | undefined,
  name: string,
): void {
  if (!chatbotId) return;
  writeCachedAgentColor(chatbotId, resolveAgentTextureColor(avatarColor, name));
}
